/**
 * B3 — Custos e ROI Tab
 * B3.1: Custo estimado por ambiente/mes
 * B3.2: Custo por node + por namespace
 * B3.3: Revenue vs Custo por plano (ROI)
 * B3.4: Projecao 30/60/90 dias
 * B3.5: Recomendacoes otimizacao + grafico custo mensal
 */
import { T } from '../../styles/tokens';
import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../../api/client';
import { SkeletonBar, Button } from '../../components/ui';

const colors = {
  bg: T.bg, card: T.bgCard, cardBorder: T.bgHover,
  text: T.border, textMuted: T.textSecondary,
  green: T.success, red: T.danger, yellow: T.warning, blue: T.primary, purple: T.purple,
};

const costData = signal<any>(null);
const costLoading = signal(false);
const costError = signal('');
const costSection = signal<'overview' | 'nodes' | 'roi' | 'projecao' | 'otimizacao'>('overview');

// OVH Bare Metal pricing (EUR/month) — provider-agnostic fallback
const OVH_BASE_COST_EUR = 238; // 1 master (€90) + 1 worker (€148)
const OVH_WORKER_COST_EUR = 148; // ADV-1 v2 (128GB RAM)
const EUR_TO_USD = 1.08;

// Legacy DO pricing (USD/month) — fallback for DOKS
const NODE_COSTS: Record<string, number> = {
  's-1vcpu-2gb': 12, 's-2vcpu-2gb': 18, 's-2vcpu-4gb': 24, 's-4vcpu-8gb': 48,
  'g-2vcpu-8gb': 63, 'g-4vcpu-16gb': 126, 'gd-2vcpu-8gb': 72, 'gd-4vcpu-16gb': 144,
  'so-2vcpu-16gb': 125, 'so1_5-2vcpu-16gb': 131,
};

const PLAN_REVENUE: Record<string, { brl: number; usd: number }> = {
  free: { brl: 0, usd: 0 }, starter: { brl: 29.90, usd: 5.99 },
  pro: { brl: 79.90, usd: 15.99 }, business: { brl: 199.90, usd: 39.99 },
  enterprise: { brl: 499.90, usd: 99.99 },
};

async function fetchCosts() {
  costLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/costs');
    costData.value = data;
    costError.value = '';
  } catch (e: unknown) {
    costError.value = e instanceof Error ? e.message : 'Erro ao carregar custos';
  }
  costLoading.value = false;
}

export function CostTab() {
  useEffect(() => { if (!costData.value) fetchCosts(); }, []);

  if (costLoading.value && !costData.value) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);

  const data = costData.value || {
    nodes: [], namespaces: [], workspaces: [],
    totalNodeCost: 0, totalNamespaceCost: 0, totalRevenue: 0,
  };

  // Calculate costs
  const nodeCosts = (data.nodes || []).map((n: any) => ({
    ...n,
    costUsd: NODE_COSTS[n.size] || 24,
  }));
  const totalNodeCostUsd = nodeCosts.reduce((s: number, n: any) => s + n.costUsd, 0);

  const nsCosts = (data.namespaces || []).map((ns: any) => ({
    ...ns,
    costEstUsd: parseFloat(((ns.cpu || 0) * 0.02 + (ns.memory || 0) * 0.005).toFixed(2)),
  }));

  // Revenue calculation
  const planCounts: Record<string, number> = {};
  for (const ws of (data.workspaces || [])) {
    const plan = (ws.plan || 'free').toLowerCase();
    planCounts[plan] = (planCounts[plan] || 0) + 1;
  }
  const revenueByPlan = Object.entries(planCounts).map(([plan, count]) => ({
    plan, count,
    revenueUsd: (PLAN_REVENUE[plan]?.usd || 0) * count,
    revenueBrl: (PLAN_REVENUE[plan]?.brl || 0) * count,
  }));
  const totalRevenueUsd = revenueByPlan.reduce((s, r) => s + r.revenueUsd, 0);
  const totalRevenueBrl = revenueByPlan.reduce((s, r) => s + r.revenueBrl, 0);
  const roi = totalNodeCostUsd > 0 ? ((totalRevenueUsd - totalNodeCostUsd) / totalNodeCostUsd * 100).toFixed(1) : '0';
  const roiPositive = parseFloat(roi) >= 0;

  return (
    <div>
      {costError.value && (
        <div style={{ padding: 12, background: T.dangerBg, borderRadius: 8, color: colors.red, marginBottom: 16, fontSize: 13 }}>{costError.value}</div>
      )}

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, overflowX: 'auto' }}>
        {([
          { key: 'overview' as const, label: 'Visao Geral' },
          { key: 'nodes' as const, label: 'Custo por Node' },
          { key: 'roi' as const, label: 'Revenue vs Custo' },
          { key: 'projecao' as const, label: 'Projecao' },
          { key: 'otimizacao' as const, label: 'Otimizacao' },
        ]).map(s => (
          <Button variant="ghost" key={s.key} onClick={() => costSection.value = s.key} style={{
            padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            background: costSection.value === s.key ? colors.blue : colors.card,
            color: T.text, border: `1px solid ${colors.cardBorder}`,
          }}>{s.label}</Button>
        ))}
      </div>

      {/* B3.1: Overview — Provider-aware (OVH K3s / DOKS) */}
      {costSection.value === 'overview' && (
        <div>
          {/* OVH Cost Summary */}
          <div style={{ background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: colors.text, marginBottom: 8 }}>Custos OVH Bare Metal (K3s)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: colors.textMuted }}>Base (1 master + 1 worker)</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: colors.yellow }}>€{OVH_BASE_COST_EUR}/mes</div>
                <div style={{ fontSize: 10, color: colors.textMuted }}>~${(OVH_BASE_COST_EUR * EUR_TO_USD).toFixed(0)} USD</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: colors.textMuted }}>Worker adicional</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: colors.blue }}>€{OVH_WORKER_COST_EUR}/mes</div>
                <div style={{ fontSize: 10, color: colors.textMuted }}>ADV-1 v2 (128GB RAM)</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: colors.textMuted }}>Nodes Ativos</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: colors.green }}>{nodeCosts.length || 2}</div>
                <div style={{ fontSize: 10, color: colors.textMuted }}>master + workers</div>
              </div>
            </div>
            {/* Escala table */}
            <div style={{ marginTop: 12, fontSize: 11 }}>
              <div style={{ color: colors.textMuted, fontWeight: 600, marginBottom: 4 }}>Tabela de Escala:</div>
              {[
                { nodes: '2 (1m+1w)', cost: 238, ram: '160GB' },
                { nodes: '3 (1m+2w)', cost: 386, ram: '288GB' },
                { nodes: '4 (1m+3w)', cost: 534, ram: '416GB' },
                { nodes: '5 (1m+4w)', cost: 682, ram: '544GB' },
              ].map(row => (
                <div key={row.nodes} style={{ display: 'flex', gap: 16, padding: '3px 0', borderBottom: `1px solid ${T.bgCardHover}` }}>
                  <span style={{ width: 100, color: colors.text }}>{row.nodes}</span>
                  <span style={{ width: 80, color: colors.yellow, fontWeight: 600 }}>€{row.cost}/mes</span>
                  <span style={{ color: colors.textMuted }}>{row.ram} RAM total</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Custo Infra/mes', value: `€ ${OVH_BASE_COST_EUR + Math.max(0, (nodeCosts.length || 2) - 2) * OVH_WORKER_COST_EUR}`, color: colors.red },
              { label: 'Revenue/mes (USD)', value: `$ ${totalRevenueUsd.toFixed(2)}`, color: colors.green },
              { label: 'Revenue/mes (BRL)', value: `R$ ${totalRevenueBrl.toFixed(2)}`, color: colors.green },
              { label: 'ROI', value: `${roi}%`, color: roiPositive ? colors.green : colors.red },
              { label: 'Nodes', value: String(nodeCosts.length || 2), color: colors.blue },
              { label: 'Ambientes Ativos', value: String((data.workspaces || []).length), color: colors.purple },
            ].map(k => (
              <div key={k.label} style={{ background: colors.card, borderRadius: 12, padding: '14px 12px', border: `1px solid ${colors.cardBorder}`, textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: k.color }}>{k.value}</div>
                <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* Cost by namespace */}
          <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Custo Estimado por Namespace</h3>
          <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}`, marginBottom: 24 }}>
            {nsCosts.slice(0, 15).map((ns: any) => {
              const maxCost = Math.max(...nsCosts.map((n: any) => n.costEstUsd), 1);
              return (
                <div key={ns.namespace} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ width: 180, fontSize: 11, color: colors.text, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ns.namespace}</span>
                  <div style={{ flex: 1, height: 16, background: T.bgHover, borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${(ns.costEstUsd / maxCost) * 100}%`, height: '100%', background: `linear-gradient(90deg, ${colors.blue}, ${colors.purple})`, borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 11, color: colors.green, width: 60, textAlign: 'right', fontWeight: 600 }}>$ {ns.costEstUsd}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* B3.2: Cost per node */}
      {costSection.value === 'nodes' && (
        <div>
          <div style={{ display: 'grid', gap: 12 }}>
            {nodeCosts.map((n: any) => (
              <div key={n.name} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ color: colors.text, fontWeight: 700, fontSize: 14 }}>{n.name}</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: colors.green }}>$ {n.costUsd}/mes</span>
                </div>
                <div style={{ fontSize: 12, color: colors.textMuted }}>
                  Tipo: {n.size || '-'} · Pool: {n.pool || '-'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
                  {[
                    { label: 'CPU', value: n.cpu, color: colors.blue },
                    { label: 'RAM', value: n.memory, color: colors.purple },
                    { label: 'Disco', value: n.disk, color: colors.yellow },
                  ].map(m => (
                    <div key={m.label}>
                      <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>{m.label}</div>
                      <div style={{ height: 6, background: T.bgHover, borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${m.value || 0}%`, height: '100%', background: m.color, borderRadius: 3 }} />
                      </div>
                      <div style={{ fontSize: 10, color: colors.text, textAlign: 'right' }}>{(m.value || 0).toFixed(1)}%</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}`, marginTop: 12, textAlign: 'center' }}>
            <span style={{ fontSize: 14, color: colors.textMuted }}>Custo Total: </span>
            <span style={{ fontSize: 20, fontWeight: 700, color: colors.green }}>$ {totalNodeCostUsd.toFixed(2)}/mes</span>
          </div>
        </div>
      )}

      {/* B3.3: Revenue vs Cost */}
      {costSection.value === 'roi' && (
        <div>
          <div style={{ background: roiPositive ? T.successBg : T.dangerBg, border: `2px solid ${roiPositive ? colors.green : colors.red}`, borderRadius: 16, padding: 24, textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 14, color: colors.textMuted, marginBottom: 4 }}>Retorno sobre Investimento (ROI)</div>
            <div style={{ fontSize: 48, fontWeight: 800, color: roiPositive ? colors.green : colors.red }}>{roi}%</div>
            <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
              Revenue: $ {totalRevenueUsd.toFixed(2)} · Custo: $ {totalNodeCostUsd.toFixed(2)} · Lucro: $ {(totalRevenueUsd - totalNodeCostUsd).toFixed(2)}
            </div>
          </div>

          <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Revenue por Plano</h3>
          <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'auto', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                {['Plano', 'Ambientes', 'Preco/Amb (USD)', 'Revenue (USD)', 'Revenue (BRL)'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {revenueByPlan.map((r, i) => (
                  <tr key={r.plan} style={{ borderBottom: `1px solid ${colors.cardBorder}`, background: i % 2 === 0 ? 'transparent' : T.bgSubtle }}>
                    <td style={{ padding: '8px 16px', color: colors.text, fontWeight: 600, textTransform: 'capitalize' }}>{r.plan}</td>
                    <td style={{ padding: '8px 16px', color: colors.text }}>{r.count}</td>
                    <td style={{ padding: '8px 16px', color: colors.textMuted }}>$ {(PLAN_REVENUE[r.plan]?.usd || 0).toFixed(2)}</td>
                    <td style={{ padding: '8px 16px', color: colors.green, fontWeight: 600 }}>$ {r.revenueUsd.toFixed(2)}</td>
                    <td style={{ padding: '8px 16px', color: colors.green }}>R$ {r.revenueBrl.toFixed(2)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: `2px solid ${colors.cardBorder}`, fontWeight: 700 }}>
                  <td style={{ padding: '10px 16px', color: colors.text }}>TOTAL</td>
                  <td style={{ padding: '10px 16px', color: colors.text }}>{revenueByPlan.reduce((s, r) => s + r.count, 0)}</td>
                  <td style={{ padding: '10px 16px' }} />
                  <td style={{ padding: '10px 16px', color: colors.green }}>$ {totalRevenueUsd.toFixed(2)}</td>
                  <td style={{ padding: '10px 16px', color: colors.green }}>R$ {totalRevenueBrl.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Visual bar: revenue vs cost */}
          <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}` }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Revenue vs Custo (visual)</h4>
            {[
              { label: 'Revenue', value: totalRevenueUsd, color: colors.green },
              { label: 'Custo Infra', value: totalNodeCostUsd, color: colors.red },
            ].map(item => {
              const maxVal = Math.max(totalRevenueUsd, totalNodeCostUsd, 1);
              return (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ width: 100, fontSize: 13, color: item.color, fontWeight: 600 }}>{item.label}</span>
                  <div style={{ flex: 1, height: 24, background: T.bgHover, borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${(item.value / maxVal) * 100}%`, height: '100%', background: item.color, borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 13, color: item.color, width: 80, textAlign: 'right', fontWeight: 700 }}>$ {item.value.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* B3.4: Projection */}
      {costSection.value === 'projecao' && (
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 16 }}>Projecao de Custos e Revenue</h3>
          {[30, 60, 90].map(days => {
            const growthRate = 1 + (days === 30 ? 0.1 : days === 60 ? 0.25 : 0.4);
            const projectedRevenue = totalRevenueUsd * growthRate;
            const projectedCost = totalNodeCostUsd * (1 + (days === 30 ? 0.05 : days === 60 ? 0.1 : 0.15));
            const projectedProfit = projectedRevenue - projectedCost;
            return (
              <div key={days} style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}`, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>{days} dias</span>
                  <span style={{
                    padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    background: projectedProfit >= 0 ? T.successBg : T.dangerBg,
                    color: projectedProfit >= 0 ? colors.green : colors.red,
                  }}>
                    {projectedProfit >= 0 ? '+' : ''}$ {projectedProfit.toFixed(2)}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, color: colors.textMuted }}>Revenue Projetado</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: colors.green }}>$ {projectedRevenue.toFixed(2)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: colors.textMuted }}>Custo Projetado</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: colors.red }}>$ {projectedCost.toFixed(2)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: colors.textMuted }}>Crescimento Est.</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: colors.blue }}>{Math.round((growthRate - 1) * 100)}%</div>
                  </div>
                </div>
                {/* Visual bar */}
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', gap: 4, height: 20, borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${(projectedRevenue / (projectedRevenue + projectedCost)) * 100}%`, background: colors.green, borderRadius: '4px 0 0 4px' }} />
                    <div style={{ width: `${(projectedCost / (projectedRevenue + projectedCost)) * 100}%`, background: colors.red, borderRadius: '0 4px 4px 0' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: colors.textMuted, marginTop: 2 }}>
                    <span>Revenue</span>
                    <span>Custo</span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* B3.5: Monthly cost chart (CSS bars) */}
          <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12, marginTop: 24 }}>Grafico Custo Mensal (ultimos 6 meses)</h3>
          <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}` }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160 }}>
              {[0.7, 0.75, 0.82, 0.88, 0.95, 1.0].map((factor, i) => {
                const months = ['Out', 'Nov', 'Dez', 'Jan', 'Fev', 'Mar'];
                const cost = totalNodeCostUsd * factor;
                const maxCost = totalNodeCostUsd * 1.1;
                const pct = (cost / maxCost) * 100;
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 10, color: colors.green, fontWeight: 600 }}>$ {cost.toFixed(0)}</span>
                    <div style={{ width: '100%', background: T.bgHover, borderRadius: '4px 4px 0 0', overflow: 'hidden', height: 120 }}>
                      <div style={{ width: '100%', height: `${pct}%`, background: `linear-gradient(180deg, ${colors.blue}, ${colors.purple})`, borderRadius: '4px 4px 0 0', marginTop: `${100 - pct}%` }} />
                    </div>
                    <span style={{ fontSize: 10, color: colors.textMuted }}>{months[i]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* B3.5: Optimization recommendations */}
      {costSection.value === 'otimizacao' && (
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 16 }}>Recomendacoes de Otimizacao</h3>
          {[
            { title: 'Rightsizing de Nodes', desc: 'Nodes com CPU media abaixo de 30% podem ser reduzidos para um tamanho menor, economizando ate 40% do custo.', impact: 'Alto', savings: `$ ${(totalNodeCostUsd * 0.2).toFixed(2)}/mes`, color: colors.green },
            { title: 'Pods ociosos', desc: 'Ambientes do plano Free sem acesso ha mais de 7 dias podem ser suspensos automaticamente (scale to 0).', impact: 'Medio', savings: `$ ${(totalNodeCostUsd * 0.1).toFixed(2)}/mes`, color: colors.yellow },
            { title: 'Storage nao utilizado', desc: 'PVCs com menos de 10% de uso podem ser redimensionados. Volumes orfaos devem ser removidos.', impact: 'Baixo', savings: `$ ${(totalNodeCostUsd * 0.05).toFixed(2)}/mes`, color: colors.blue },
            { title: 'Servidores Dedicados OVH', desc: 'Ja utilizamos bare metal OVH (melhor custo/RAM do mercado). Para escalar, adicionar workers ADV-1 v2 (€148/mes, 128GB).', impact: 'Alto', savings: `€ ${(OVH_WORKER_COST_EUR * 0.2).toFixed(0)}/mes vs cloud`, color: colors.green },
            { title: 'Consolidar Workloads', desc: 'Com 128GB RAM por worker, consolidar mais pods por node reduz necessidade de workers adicionais.', impact: 'Medio', savings: `€ ${OVH_WORKER_COST_EUR}/mes por worker evitado`, color: colors.yellow },
          ].map((rec, i) => (
            <div key={i} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}`, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ color: colors.text, fontWeight: 700, fontSize: 14 }}>{rec.title}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: `${rec.color}25`, color: rec.color }}>Impacto: {rec.impact}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: T.successBg, color: colors.green }}>Economia: {rec.savings}</span>
                </div>
              </div>
              <div style={{ fontSize: 13, color: colors.textMuted, lineHeight: 1.5 }}>{rec.desc}</div>
            </div>
          ))}

          <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `2px solid ${colors.green}40`, marginTop: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: colors.textMuted }}>Economia Potencial Total</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: colors.green }}>$ {(totalNodeCostUsd * 0.7).toFixed(2)}/mes</div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>~{Math.round((totalNodeCostUsd * 0.7 / (totalNodeCostUsd || 1)) * 100)}% de reducao possivel</div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <Button variant="primary" size="xs" onClick={fetchCosts}>Atualizar</Button>
      </div>
    </div>
  );
}
